<template>
  <div
    v-if="plans"
    :class="['container', $style.container, { [$style.scrollable]: hasCustomPlans }]"
  >
    <div :class="$style.list">
      <transition-group name="fade">
        <SubscrbPlanItemSkeleton
          v-show="loading"
          v-for="i of 5"
          :key="`skeleton-${i}`"
        />
      </transition-group>
      <SubscrbPlanItem
        v-show="!loading && plan"
        v-for="(plan, index) of plansToShow"
        :key="plan._id || index"
        :plan="plan"
        :user="user"
        :all-plans="starterNames.includes(plan.name) ? starterPlans : [plan]"
        :is-active="modelValue === plan._id"
        @click.prevent="updateValue(plan._id)"
        @action="onPlanAction"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PlanItemDto } from '~/dto';
import type { SubscrbListParams } from './index.js';
import { computed } from 'vue';

defineProps<SubscrbListParams>();
const emits = defineEmits(['update:modelValue', 'action']);
const plans = inject<Ref<PlanItemDto[] | null>>('plans');
const starterNames = ['Starter-1', 'Starter-2', 'Starter-3'];

const plansArr = computed(() => Array.isArray(plans?.value) ? plans.value : []);
const starterPlans = computed(() => plansArr.value.filter(p => starterNames.includes(p.name)));
const order = ['Free', 'Lite', 'Starter-1', 'Personal', 'Team', 'Professional'];

const customPlans = computed(() => plansArr.value.filter(p => p.isCustom));
const hasCustomPlans = computed(() => customPlans.value.length > 0);

const plansToShow = computed(() => {
  const orderedPlans = order.flatMap(name => {
    if (starterNames.includes(name)) {
      const plan = starterPlans.value.find(p => p.name === name);
      return plan ? [plan] : [];
    }
    const plan = plansArr.value.find(p => p.name === name);
    return plan ? [plan] : [];
  });

  const knownIds = new Set([
    ...orderedPlans.map(p => p._id),
    ...starterPlans.value.map(p => p._id),
    ...customPlans.value.map(p => p._id),
  ]);
  const remainingPlans = plansArr.value.filter(
    p => !knownIds.has(p._id) && !p.isCustom && !starterNames.includes(p.name),
  );

  return [...orderedPlans, ...remainingPlans, ...customPlans.value];
});

const updateValue = (value: string): void => {
  emits('update:modelValue', value);
}

const onPlanAction = (planId: string) => {
  emits('action', planId);
};
</script>

<style lang="scss" module>
  .container {
    width: 100%;
    overflow: hidden;

    &.scrollable {
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
      padding-bottom: 8px;
      
      // Кастомный скроллбар для WebKit (Chrome, Safari, Edge)
      &::-webkit-scrollbar {
        height: 8px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 4px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
        transition: background 0.2s ease;
        
        &:hover {
          background: #a8a8a8;
        }
      }
      
      // Кастомный скроллбар для Firefox
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f5f5f5;
    }

    @media (max-width: 1320px) {
      overflow-x: auto !important;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
      padding-bottom: 8px;
      
      // Кастомный скроллбар для WebKit (Chrome, Safari, Edge)
      &::-webkit-scrollbar {
        height: 8px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 4px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
        transition: background 0.2s ease;
        
        &:hover {
          background: #a8a8a8;
        }
      }
      
      // Кастомный скроллбар для Firefox
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f5f5f5;
    }
  }

  .list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 7px;
    justify-content: center;
    align-items: stretch;

    @media (min-width: 768px) and (max-width: 1320px) {
      width: 1320px;
    }

    @media (max-width: 767px) {
      width: 100%;
      grid-template-columns: repeat(2, 1fr);
      overflow: visible;
    }
  }

  .container {
    @media (max-width: 767px) {
      overflow: visible !important;
    }
  }

  .container.scrollable .list {
    grid-auto-flow: column;
    grid-auto-columns: 210px;
    grid-template-columns: none;
    justify-content: start;

    @media (max-width: 767px) {
      grid-auto-flow: unset;
      grid-auto-columns: unset;
      grid-template-columns: repeat(2, 1fr);
      justify-content: unset;
    }
  }
</style>